import React, { useState, useMemo, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  useTheme,
  Tooltip,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Divider,
  TextField,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import Switch from "@mui/material/Switch";
import { useMutation } from "@apollo/client";
import {
  DELETE_INTEGRATION,
  SET_INTEGRATION_USE_AS_IDP,
  GENERATE_TRUTO_REAUTH_TOKEN,
  UPDATE_INTEGRATION_REGIONS,
} from "../../../../apis/settingsApis/integration-apis/integrationApis";
import { DISABLE_WATCH_TOWER } from "../../../../apis/settingsApis/assets-apis/assetsApis";
import authenticate from "@truto/truto-link-sdk";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import SystemOwnersDialog from "./SystemOwnersDialog";
import { formatIntegrationLabel } from "../utils/integrationUtils";
import {
  DomainType,
  MasterLookupItemsType,
  AWS_REGIONS,
} from "../../../../utils/enums";

const CLOUD_INTEGRATION_KEYS = ["aws", "azure", "gcp"];
const WATCH_TOWER_KEY = "rego_watch_tower";

/**
 * Management Drawer for handling multiple integration connections
 * Shows list of connected accounts with ability to delete and add new connections
 */
const IntegrationManagementDrawer = ({
  open,
  onClose,
  integration,
  onConnectNew,
  onDeleteSuccess,
  onShowSnackbar,
  ownersByResourceId = {},
  currentEmployeeDataId,
  isAdmin,
  onRefetchSystemOwners,
  onRefetchIntegrations,
  idpIntegrationCount = 0,
}) => {
  const theme = useTheme();

  const isInfraIntegration = useMemo(() => {
    if (!integration?.key) return false;
    if (CLOUD_INTEGRATION_KEYS.includes(integration.key.toLowerCase()))
      return true;
    return integration.domainKey === DomainType.INFRA;
  }, [integration]);

  // Rego WatchTower is agent-based — disconnect via its own soft-delete mutation,
  // and (like infra) show the per-account delete action.
  const isWatchTower = integration?.key === WATCH_TOWER_KEY;

  const [disableWatchTower, { loading: disablingWatchTower }] =
    useMutation(DISABLE_WATCH_TOWER);

  const canConnectAnother = useMemo(() => {
    if (!integration) return false;
    if (isInfraIntegration) return true;
    if (integration.domainKey === MasterLookupItemsType.HRMS_PROVIDER) return false;
    return true;
  }, [integration, isInfraIntegration]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccountForDeletion, setSelectedAccountForDeletion] =
    useState(null);
  const [addAccountHover, setAddAccountHover] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [ownersDialogAccount, setOwnersDialogAccount] = useState(null);

  useEffect(() => {
    if (!open) setOwnersDialogAccount(null);
  }, [open]);

  const [deleteIntegration, { loading: deleting }] =
    useMutation(DELETE_INTEGRATION);

  // "Use as identity provider" override -----------------------------------
  const [useAsIdp, setUseAsIdp] = useState(false);
  useEffect(() => {
    setUseAsIdp(Boolean(integration?.useAsIdp));
  }, [integration]);

  const [setIntegrationUseAsIdp, { loading: savingIdp }] = useMutation(
    SET_INTEGRATION_USE_AS_IDP
  );

  const [generateTrutoReauthToken] = useMutation(GENERATE_TRUTO_REAUTH_TOKEN);
  const [reauthLoadingRciId, setReauthLoadingRciId] = useState(null);

  // Region editing (AWS only)
  const [regionsDialogAccount, setRegionsDialogAccount] = useState(null);
  const [draftRegions, setDraftRegions] = useState([]);
  const [updateIntegrationRegions, { loading: savingRegions }] = useMutation(
    UPDATE_INTEGRATION_REGIONS
  );

  const isAws = integration?.key?.toLowerCase() === "aws";

  const openRegionsDialog = (row) => {
    let meta = row.metadata;
    if (typeof meta === "string") {
      try { meta = JSON.parse(meta); } catch { meta = null; }
    }
    const current = row.regions || meta?.aws?.regions || [];
    setDraftRegions(
      AWS_REGIONS.filter((option) => current.includes(option.value))
    );
    setRegionsDialogAccount(row);
  };

  const handleSaveRegions = async () => {
    try {
      const result = await updateIntegrationRegions({
        variables: {
          id: regionsDialogAccount.id,
          regions: draftRegions.map((r) => r.value),
        },
      });

      const response = result?.data?.updateIntegrationRegions;
      onShowSnackbar?.({
        message: response?.message || "Regions updated.",
        vertical: "top",
        horizontal: "right",
      });

      if (response?.success) {
        setRegionsDialogAccount(null);
        await onRefetchIntegrations?.();
      }
    } catch (err) {
      console.error("Failed to update regions:", err);
      onShowSnackbar?.({
        message: "Failed to update regions. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleReauthClick = async (row) => {
    if (!row?.id) return;
    setReauthLoadingRciId(row.id);
    try {
      const { data } = await generateTrutoReauthToken({
        variables: { rciId: row.id },
      });
      const linkToken = data?.generateTrutoReauthToken?.linkToken;
      if (!linkToken) {
        onShowSnackbar({
          message:
            data?.generateTrutoReauthToken?.message ||
            "Failed to start reauthorization.",
          vertical: "top",
          horizontal: "right",
        });
        return;
      }
      await authenticate(linkToken, {
        noBack: true,
        skipRapidForm: false,
        iframe: true,
      });
      if (onRefetchIntegrations) await onRefetchIntegrations();
    } catch (err) {
      onShowSnackbar({
        message: "Reauthorization failed. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setReauthLoadingRciId(null);
    }
  };


  const hasActiveConnection = useMemo(
    () =>
      (integration?.connectedAccounts || []).some(
        (acc) => acc.is_active === true || acc.is_active === "true"
      ),
    [integration]
  );
  const canConfigureAsIdp =
    isAdmin &&
    integration?.idpEligible === true &&
    integration?.domainKey !== MasterLookupItemsType.IDP_PROVIDER &&
    integration?.domainKey !== MasterLookupItemsType.HRMS_PROVIDER &&
    integration?.integrationId != null &&
    hasActiveConnection;

  const cannotDisable = useAsIdp && idpIntegrationCount <= 1;

  const handleToggleUseAsIdp = async (event) => {
    const next = event.target.checked;
    if (next === false && idpIntegrationCount <= 1) {
      onShowSnackbar({
        message:
          "Cannot disable: this is your only employee data source. Add another source first.",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }
    const activeConnection = integration?.connectedAccounts?.find(
      (acc) => acc.is_active === true || acc.is_active === "true"
    );
    if (!activeConnection?.id) {
      onShowSnackbar({
        message: "No active connection found for this integration.",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }
    setUseAsIdp(next); // optimistic
    try {
      const result = await setIntegrationUseAsIdp({
        variables: {
          rciId: activeConnection.id,
          useAsIdp: next,
        },
      });
      const payload = result?.data?.setIntegrationUseAsIdp;
      if (payload?.success) {
        onShowSnackbar({
          message:
            payload.message ||
            (next
              ? "Integration marked as employee data source."
              : "Integration is no longer used as an employee data source."),
          vertical: "top",
          horizontal: "right",
        });
        if (onRefetchIntegrations) await onRefetchIntegrations();
      } else {
        setUseAsIdp(!next); // revert
        onShowSnackbar({
          message:
            payload?.message ||
            "Failed to update employee data source setting.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error toggling use_as_idp:", error);
      setUseAsIdp(!next); // revert
      onShowSnackbar({
        message: "An error occurred while updating the setting.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (!integration || !integration.connectedAccounts) {
      return { total: 0, active: 0, inactive: 0 };
    }

    const connectedAccounts = integration.connectedAccounts;
    const total = connectedAccounts.length;
    const active = connectedAccounts.filter(
      (acc) => acc.is_active === true || acc.is_active === "true"
    ).length;
    const inactive = total - active;

    return { total, active, inactive };
  }, [integration]);

  // Transform connected accounts data for table
  const tableData = useMemo(() => {
    if (!integration || !integration.connectedAccounts) {
      return [];
    }

    return integration.connectedAccounts.map((account, index) => ({
      id: account.id || `account-${index}`,
      accountLabel:
        account.account_label ||
        account.account_email ||
        `Account ${index + 1}`,
      isActive: account.is_active === true || account.is_active === "true",
      inactivityReason: account.inactivity_reason,
      // Keep original account object for operations
      ...account,
    }));
  }, [integration]);

  // Filter accounts based on search query
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) {
      return tableData;
    }

    const query = searchQuery.toLowerCase();
    return tableData.filter((account) =>
      account.accountLabel?.toLowerCase().includes(query)
    );
  }, [tableData, searchQuery]);

  const columns = useMemo(() => {
    const accountCol = {
      label: "Account",
      field: "accountLabel",
      align: "left",
      width: isInfraIntegration ? "36%" : "40%",
    };
    const statusCol = {
      label: "Status",
      field: "status",
      align: "left",
      width: "20%",
    };
    const ownersCol = {
      label: "Data owners",
      field: "systemOwners",
      align: "left",
      width: isInfraIntegration ? "26%" : "24%",
    };
    const actionCol = {
      label: "Actions",
      field: "actions",
      align: "right",
      width: isInfraIntegration ? "18%" : "16%",
    };
    // main made the action column unconditional, which already covers the case
    // this branch added it for (infra and Watch Tower) — so the condition goes
    // rather than being merged back in. `isWatchTower` is still used below, for
    // routing the disconnect through disableWatchTower.
    return [accountCol, statusCol, ownersCol, actionCol];
  }, [isInfraIntegration]);

  if (!integration) return null;

  const { label, logo_path, connectedAccounts = [] } = integration;

  const handleDeleteClick = (account) => {
    setSelectedAccountForDeletion(account);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAccountForDeletion) return;

    // Rego WatchTower disconnects via its own agent-integration mutation
    // (no Truto account id to delete by).
    if (isWatchTower) {
      try {
        const result = await disableWatchTower();
        if (result?.data?.disableWatchTower?.success) {
          setDeleteDialogOpen(false);
          setSelectedAccountForDeletion(null);
          onShowSnackbar({
            message: "Rego WatchTower disconnected.",
            vertical: "top",
            horizontal: "right",
          });
          if (onDeleteSuccess) onDeleteSuccess();
        } else {
          onShowSnackbar({
            message:
              result?.data?.disableWatchTower?.message ||
              "Failed to disconnect Rego WatchTower.",
            vertical: "top",
            horizontal: "right",
          });
        }
      } catch (error) {
        console.error("Error disconnecting Rego WatchTower:", error);
        onShowSnackbar({
          message: "An error occurred while disconnecting Rego WatchTower.",
          vertical: "top",
          horizontal: "right",
        });
      }
      return;
    }

    try {
      const result = await deleteIntegration({
        variables: {
          id: selectedAccountForDeletion.id,
        },
      });

      if (result?.data?.deleteIntegrationById?.success) {
        setDeleteDialogOpen(false);
        setSelectedAccountForDeletion(null);

        onShowSnackbar({
          message: "Integration deleted successfully",
          vertical: "top",
          horizontal: "right",
        });

        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
      } else {
        onShowSnackbar({
          message: "Failed to delete integration. Please contact support.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error deleting integration:", error);

      onShowSnackbar({
        message:
          "An error occurred while deleting the integration. Please contact support.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedAccountForDeletion(null);
  };

  const handleConnectNew = () => {
    if (onConnectNew) {
      onConnectNew(integration);
    }
  };

  // Render cell content
  const renderCell = (row, column) => {
    switch (column.field) {
      case "accountLabel":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              {row.accountLabel}
            </Typography>
          </Box>
        );

      case "systemOwners": {
        const resourceId = row.integration_resource_id;
        const owners = resourceId
          ? ownersByResourceId[resourceId] || []
          : [];
        if (!resourceId || !row.isActive) {
          return (
            <Typography variant="caption" color="text.secondary">
              -
            </Typography>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Chip
              size="small"
              label={
                owners.length === 0
                  ? "Unassigned"
                  : `${owners.length} owner${owners.length === 1 ? "" : "s"}`
              }
              sx={{
                height: "22px",
                fontWeight: 500,
                fontSize: "0.75rem",
                borderRadius: 1,
                backgroundColor: owners.length === 0
                  ? theme.customColors.status.warning?.bg || theme.customColors.background.light
                  : theme.palette._blue?.light,
                color: owners.length === 0
                  ? theme.customColors.status.warning?.text || theme.customColors.text.secondary
                  : theme.palette._blue?.main,
                border: "none",
                "& .MuiChip-label": { px: 1 },
              }}
            />
            <Tooltip title="Manage data owners" arrow>
              <IconButton
                size="small"
                onClick={() => setOwnersDialogAccount(row)}
                sx={{
                  color: theme.customColors.text.secondary,
                  width: 28,
                  height: 28,
                  "&:hover": {
                    backgroundColor: theme.customColors.background.hover,
                    color: theme.customColors.icons.primary,
                  },
                }}
              >
                <GroupOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }

      case "status":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            {!row.isActive ? (
              <Tooltip
                title={
                  row.inactivityReason ||
                  "Reconnect required to resume syncing."
                }
                arrow
              >
                <Chip
                  icon={<ErrorOutlineIcon fontSize="small" />}
                  label="Needs reauth"
                  size="small"
                  sx={{
                    height: "24px",
                    backgroundColor: theme.customColors.status.error.bg,
                    color: theme.customColors.status.error.text,
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    border: "none",
                    borderRadius: 1,
                    "& .MuiChip-icon": {
                      color: theme.customColors.status.error.text,
                      marginLeft: "6px",
                    },
                  }}
                />
              </Tooltip>
            ) : (
              <Chip
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                label="Active"
                size="small"
                sx={{
                  height: "24px",
                  backgroundColor: theme.customColors.status.success.bg,
                  color: theme.customColors.status.success.text,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  border: "none",
                  borderRadius: 1,
                  "& .MuiChip-icon": {
                    color: theme.customColors.status.success.text,
                    marginLeft: "6px",
                  },
                }}
              />
            )}
          </Box>
        );

      case "actions":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
              width: "100%",
            }}
          >
            {isAws && row.isActive && (
              <Tooltip
                title={
                  row.regions?.length
                    ? `Scanning ${row.regions.length} region(s)`
                    : "Scanning all regions"
                }
                arrow
              >
                <IconButton
                  size="small"
                  onClick={() => openRegionsDialog(row)}
                  sx={{
                    color: theme.customColors.text.subtle,
                    "&:hover": {
                      backgroundColor: theme.customColors.background.hover,
                    },
                  }}
                >
                  <PublicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!row.isActive && !isInfraIntegration ? (
              <Tooltip title="Reauthorize connection" arrow>
                <IconButton
                  size="small"
                  onClick={() => handleReauthClick(row)}
                  disabled={reauthLoadingRciId === row.id}
                  sx={{
                    color: theme.customColors.status.error.text,
                    "&:hover": {
                      backgroundColor: theme.customColors.background.hover,
                    },
                    "&:disabled": {
                      color: theme.customColors.text.disabled,
                    },
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                    "& .rotating-icon": {
                      animation: "spin 1s linear infinite",
                    },
                  }}
                >
                  <RefreshIcon
                    fontSize="small"
                    className={
                      reauthLoadingRciId === row.id ? "rotating-icon" : ""
                    }
                  />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title="Remove connection" arrow>
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(row)}
                disabled={deleting || disablingWatchTower}
                sx={{
                  color: theme.customColors.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.customColors.background.hover,
                    color: theme.customColors.status.error.main,
                  },
                  "&:disabled": {
                    color: theme.customColors.text.disabled,
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );

      default:
        return row[column.field];
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "50%",
            backgroundColor: theme.customColors.background.main,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#FFFFFF",
            borderBottom: `1px solid ${theme.customColors.border.subtle}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 4,
              py: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Logo */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "10px",
                  backgroundColor: theme.customColors.background.cardHeader,
                  border: `1px solid ${theme.customColors.border.main}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {logo_path ? (
                  <img
                    src={`${process.env.REACT_APP_POLICY_DOC_URL}/logos/${logo_path}`}
                    alt={formatIntegrationLabel(label)}
                    style={{ width: 32, height: 32, objectFit: "contain" }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    {formatIntegrationLabel(label)?.charAt(0) || "?"}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: theme.customColors.text.strong,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Manage {formatIntegrationLabel(label)} Connections
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.secondary,
                    mt: 0.5,
                  }}
                >
                  View, manage, and add new connections for this integration
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{ color: theme.customColors.text.secondary }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            px: 4,
            py: 3,
            backgroundColor: theme.customColors.background.main,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.customColors.border.main,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.customColors.border.strong,
            },
          }}
        >
          {/* Identity Provider Card — let a customer extract this source's
              directory users into their people directory. */}
          {canConfigureAsIdp && (
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #E8EAED",
                mb: 3,
              }}
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    color={theme.customColors.text.strong}
                  >
                    Employee data source
                  </Typography>
                }
                sx={{
                  borderBottom: "1px solid #ddd",
                  background: "#F7F7FC",
                  ".MuiCardHeader-title": {
                    fontSize: "16px",
                    fontWeight: "bold",
                  },
                  padding: "14px",
                }}
              />
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.customColors.text.strong,
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Use as employee data source
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.customColors.text.secondary }}
                    >
                      Treat the users from this integration as employees and add
                      them to your people directory, so they're tracked for
                      compliance like everyone else. Use this when you're not
                      using any HRMS for managing employees, or you want to use
                      the users in this integration as a source for your
                      employee data.
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      cannotDisable
                        ? "This is your only employee data source and can't be disabled. Add another source first."
                        : ""
                    }
                    arrow
                  >
                    <span>
                      <Switch
                        checked={useAsIdp}
                        onChange={handleToggleUseAsIdp}
                        disabled={savingIdp || cannotDisable}
                        inputProps={{
                          "aria-label":
                            "Use integration as employee data source",
                        }}
                      />
                    </span>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Connected Accounts Card */}
          <Card
            sx={{
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #E8EAED",
            }}
            variant="outlined"
          >
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    color={theme.customColors.text.strong}
                  >
                    Connected Accounts
                  </Typography>
                  {searchQuery && (
                    <Chip
                      label="Filtered"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        height: 24,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: theme.customColors.text.secondary,
                        borderColor: theme.customColors.border.main,
                        background: theme.customColors.background.light,
                      }}
                    />
                  )}
                </Box>
              }
              sx={{
                borderBottom: "1px solid #ddd",
                background: "#F7F7FC",
                ".MuiCardHeader-title": {
                  fontSize: "16px",
                  fontWeight: "bold",
                },
                padding: "14px",
              }}
            />

            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 2,
                  pb: 1.5,
                  backgroundColor: theme.customColors.background.main,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <TextField
                      size="small"
                      placeholder="Search accounts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              sx={{ color: "#6B7280", fontSize: 20 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          transition: "all 0.2s ease",
                          fontSize: "14px",
                          "& fieldset": {
                            borderColor: "#E5E7EB",
                          },
                          "&:hover fieldset": {
                            borderColor: "#9CA3AF",
                            boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3B82F6",
                            boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      minWidth: 120,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        px: 3,
                        py: 1,
                        borderRadius: "10px",
                        backgroundColor: "#F3F4F6",
                        border: "1px solid #E5E7EB",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="#374151"
                        fontWeight="500"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {filteredAccounts.length}{" "}
                        {filteredAccounts.length === 1 ? "account" : "accounts"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ borderColor: theme.customColors.border.main }} />

              <Box>
                {connectedAccounts.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 12,
                    }}
                  >
                    <AssessmentIcon
                      sx={{
                        fontSize: 48,
                        color: theme.customColors.icons.disabled,
                        mb: 3,
                      }}
                    />
                    <Typography
                      variant="h5"
                      color="textSecondary"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      No accounts connected yet
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 4, maxWidth: 400, mx: "auto", lineHeight: 1.5 }}
                    >
                      Connect your first account to start managing integrations
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={handleConnectNew}
                      size="large"
                      sx={{
                        bgcolor: theme.customColors.button.primary,
                        color: theme.customColors.button.text,
                        fontWeight: 500,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "14px",
                        px: 2,
                        py: 1,
                        "&:hover": { bgcolor: theme.palette._blue?.dark },
                      }}
                    >
                      Connect New Account
                    </Button>
                  </Box>
                ) : (
                  <ReusableTable
                    columns={columns}
                    data={filteredAccounts}
                    renderCell={renderCell}
                    loading={deleting}
                    showPagination={true}
                    enableHorizontalScroll={true}
                    rowsPerPageOptions={[20, 40]}
                    height="auto"
                    sx={{
                      "& .MuiTable-root": {
                        tableLayout: "auto",
                        minWidth: "100% !important",
                        maxWidth: "100% !important",
                      },
                      "& .MuiTableCell-root": {
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "& .MuiTableRow-root:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.01)",
                      },
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#FFFFFF",
            borderTop: `1px solid ${theme.customColors.border.subtle}`,
            px: 4,
            py: 2.5,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {connectedAccounts.length > 0 && canConnectAnother && (
            <Button
              component="button"
              onClick={handleConnectNew}
              onMouseEnter={() => setAddAccountHover(true)}
              onMouseLeave={() => setAddAccountHover(false)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 12px",
                minWidth: 0,
                textTransform: "none",
                backgroundColor: addAccountHover
                  ? "rgba(58, 61, 238, 0.08)"
                  : "transparent",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                alignSelf: "flex-end",
                cursor: "pointer",
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(58, 61, 238, 0.08)",
                },
              }}
            >
              Add another account
              <ArrowForwardIosIcon sx={{ fontSize: "14px" }} />
            </Button>
          )}
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        accountLabel={selectedAccountForDeletion?.account_label}
        loading={deleting}
      />

      <SystemOwnersDialog
        open={Boolean(ownersDialogAccount)}
        onClose={() => setOwnersDialogAccount(null)}
        accountLabel={
          ownersDialogAccount?.account_label ||
          ownersDialogAccount?.accountLabel ||
          "Account"
        }
        integrationResourceId={ownersDialogAccount?.integration_resource_id}
        ownerRows={
          ownersDialogAccount?.integration_resource_id
            ? ownersByResourceId[ownersDialogAccount.integration_resource_id] ||
              []
            : []
        }
        currentEmployeeDataId={currentEmployeeDataId}
        isAdmin={isAdmin}
        onShowSnackbar={onShowSnackbar}
        onOwnersChanged={async () => {
          if (onRefetchSystemOwners) await onRefetchSystemOwners();
        }}
      />

      <Dialog
        open={Boolean(regionsDialogAccount)}
        onClose={() => setRegionsDialogAccount(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Regions to scan
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {regionsDialogAccount?.accountLabel}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={AWS_REGIONS}
            value={draftRegions}
            onChange={(event, value) => setDraftRegions(value)}
            getOptionLabel={(option) => `${option.label} — ${option.value}`}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.value}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={draftRegions.length ? "" : "All regions"}
                helperText="Leave empty to scan every region. Resources outside your selection will not be monitored. Saving re-runs all AWS checks."
              />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRegionsDialogAccount(null)}
            disabled={savingRegions}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRegions}
            disabled={savingRegions}
          >
            {savingRegions ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IntegrationManagementDrawer;
